'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

type TaskType = 'Citizen Follow-Up' | 'Agency Follow-Up' | 'Case Review' | 'AI Draft Review' | 'SLA Escalation' | 'Supervisor Review';
type TaskStatus = 'Pending' | 'In Progress' | 'Completed' | 'Overdue';

interface Task {
  id: string;
  label: string;
  type: TaskType;
  due: string;
  status: TaskStatus;
  priority: 'High' | 'Medium' | 'Low';
  linkedCase?: string;
  linkedCitizen?: string;
  assignee: string;
  aiSuggested?: boolean;
}

const allTasks: Task[] = [
  { id: 'task-001', label: 'Follow up with Kumar Rajan regarding Road Tax Renewal status', type: 'Citizen Follow-Up', due: 'Today, 14:00', status: 'Overdue', priority: 'High', linkedCase: 'MYGCC-2026-008420', linkedCitizen: 'Kumar Rajan', assignee: 'Nur Aisyah' },
  { id: 'task-002', label: 'Update case MYGCC-2026-008418 with citizen response from email', type: 'Case Review', due: 'Today, 16:30', status: 'Pending', priority: 'Medium', linkedCase: 'MYGCC-2026-008418', linkedCitizen: 'Nur Aina', assignee: 'Nur Aisyah' },
  { id: 'task-003', label: 'Review AI draft for MYGCC-2026-008415 — KWSP Withdrawal', type: 'AI Draft Review', due: 'Today, 17:00', status: 'Pending', priority: 'High', linkedCase: 'MYGCC-2026-008415', linkedCitizen: 'Muhammad Danish', assignee: 'Nur Aisyah', aiSuggested: true },
  { id: 'task-004', label: 'Escalate MYGCC-2026-008390 to Immigration supervisor', type: 'SLA Escalation', due: 'Today, 15:00', status: 'Overdue', priority: 'High', linkedCase: 'MYGCC-2026-008390', linkedCitizen: 'Priya Sundaram', assignee: 'Nur Aisyah' },
  { id: 'task-005', label: 'Follow up with Jabatan Imigresen on passport application status', type: 'Agency Follow-Up', due: 'Tomorrow, 09:00', status: 'Pending', priority: 'Medium', linkedCase: 'MYGCC-2026-008390', assignee: 'Nur Aisyah' },
  { id: 'task-006', label: 'Review and approve AI-generated case summary for INT-14892', type: 'AI Draft Review', due: 'Today, 18:00', status: 'In Progress', priority: 'Medium', linkedCase: 'MYGCC-2026-008431', linkedCitizen: 'Ahmed Firdaus', assignee: 'Nur Aisyah', aiSuggested: true },
  { id: 'task-007', label: 'Supervisor review requested for Farah Lim — Income Tax Filing', type: 'Supervisor Review', due: 'Tomorrow, 10:00', status: 'Pending', priority: 'Low', linkedCase: 'MYGCC-2026-008409', linkedCitizen: 'Farah Lim', assignee: 'Nur Aisyah' },
  { id: 'task-008', label: 'Send follow-up email to Siti Mariam regarding application status', type: 'Citizen Follow-Up', due: 'Tomorrow, 11:00', status: 'Pending', priority: 'Medium', linkedCitizen: 'Siti Mariam', assignee: 'Nur Aisyah', aiSuggested: true },
];

const typeColors: Record<TaskType, string> = {
  'Citizen Follow-Up': 'bg-blue-50 text-blue-700 border-blue-200',
  'Agency Follow-Up': 'bg-purple-50 text-purple-700 border-purple-200',
  'Case Review': 'bg-amber-50 text-amber-700 border-amber-200',
  'AI Draft Review': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'SLA Escalation': 'bg-red-50 text-red-700 border-red-200',
  'Supervisor Review': 'bg-orange-50 text-orange-700 border-orange-200',
};

const statusColors: Record<TaskStatus, string> = {
  Pending: 'bg-slate-50 text-slate-600 border-slate-200',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  Completed: 'bg-green-50 text-green-700 border-green-200',
  Overdue: 'bg-red-50 text-red-700 border-red-200',
};

export default function TasksContent() {
  const [tasks, setTasks] = useState(allTasks);
  const [filter, setFilter] = useState<'All' | 'My Tasks' | 'Overdue' | 'Due Today' | 'AI Suggested'>('All');

  const filtered = tasks.filter(t => {
    if (filter === 'Overdue') return t.status === 'Overdue';
    if (filter === 'Due Today') return t.due.startsWith('Today');
    if (filter === 'AI Suggested') return t.aiSuggested;
    return true;
  });

  const overdue = tasks.filter(t => t.status === 'Overdue').length;
  const dueToday = tasks.filter(t => t.due.startsWith('Today')).length;
  const aiSuggested = tasks.filter(t => t.aiSuggested).length;

  const completeTask = (id: string) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, status: 'Completed' as TaskStatus } : t));
    toast.success('Task marked as completed');
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-5 py-4 page-transition">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[18px] font-700 text-foreground">Tasks</h1>
          <p className="text-[12px] text-muted-foreground mt-0.5">{tasks.filter(t => t.status !== 'Completed').length} pending tasks · {overdue} overdue</p>
        </div>
        <button onClick={() => toast.info('New task created')} className="btn-primary px-3 py-1.5 flex items-center gap-1.5 text-[12px]">
          <Icon name="PlusIcon" size={13} />
          New Task
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { label: 'All Tasks', count: tasks.length, filter: 'All' as const, color: 'text-foreground', bg: 'bg-muted/50' },
          { label: 'Overdue', count: overdue, filter: 'Overdue' as const, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Due Today', count: dueToday, filter: 'Due Today' as const, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'AI Suggested', count: aiSuggested, filter: 'AI Suggested' as const, color: 'text-primary', bg: 'bg-blue-50' },
        ].map(s => (
          <button
            key={s.label}
            onClick={() => setFilter(s.filter)}
            className={`card-elevated p-3 flex items-center gap-3 text-left card-hover ${filter === s.filter ? 'border-2 border-primary' : ''}`}
          >
            <div className={`p-2 rounded-xl ${s.bg}`}>
              <span className={`text-[18px] font-700 font-tabular ${s.color}`}>{s.count}</span>
            </div>
            <div className="text-[12px] font-600 text-foreground">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1 mb-4 w-fit">
        {(['All', 'My Tasks', 'Overdue', 'Due Today', 'AI Suggested'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-600 transition-all whitespace-nowrap ${filter === f ? 'bg-white text-primary shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {f}
            {f === 'Overdue' && overdue > 0 && <span className="ml-1.5 bg-red-500 text-white text-[9px] font-700 rounded-full px-1 py-0.5">{overdue}</span>}
            {f === 'AI Suggested' && <span className="ml-1.5 ai-badge">AI</span>}
          </button>
        ))}
      </div>

      {/* Task list */}
      <div className="space-y-2">
        {filtered.map(task => (
          <div
            key={task.id}
            className={`card-elevated p-4 flex items-start gap-4 transition-all ${task.status === 'Completed' ? 'opacity-50' : 'card-hover'}`}
          >
            {/* Checkbox */}
            <button
              onClick={() => completeTask(task.id)}
              className={`w-5 h-5 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                task.status === 'Completed' ? 'bg-green-500 border-green-500' :
                task.status === 'Overdue' ? 'border-red-400' :
                task.priority === 'High' ? 'border-amber-400' : 'border-border'
              }`}
            >
              {task.status === 'Completed' && <Icon name="CheckIcon" size={11} className="text-white" />}
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 mb-2">
                <span className={`text-[13px] font-600 ${task.status === 'Completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                  {task.label}
                </span>
                {task.aiSuggested && <span className="ai-badge flex-shrink-0 mt-0.5">AI</span>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-600 px-2 py-0.5 rounded-md border ${typeColors[task.type]}`}>{task.type}</span>
                <span className={`text-[10px] font-600 px-2 py-0.5 rounded-md border ${statusColors[task.status]}`}>{task.status}</span>
                {task.linkedCase && (
                  <button onClick={() => toast.info(`Opening case ${task.linkedCase}`)} className="text-[11px] text-primary font-600 hover:underline">
                    {task.linkedCase}
                  </button>
                )}
                {task.linkedCitizen && (
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Icon name="UserIcon" size={10} />
                    {task.linkedCitizen}
                  </span>
                )}
              </div>
            </div>

            {/* Meta */}
            <div className="flex-shrink-0 text-right">
              <div className={`text-[11px] font-600 ${task.status === 'Overdue' ? 'text-red-600' : 'text-muted-foreground'}`}>
                {task.due}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{task.assignee}</div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => toast.info('Task reassigned')} className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="Reassign">
                <Icon name="UserPlusIcon" size={13} className="text-muted-foreground" />
              </button>
              <button onClick={() => toast.info('Task rescheduled')} className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="Reschedule">
                <Icon name="CalendarIcon" size={13} className="text-muted-foreground" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="card-elevated p-12 text-center">
            <Icon name="CheckCircleIcon" size={32} className="text-muted-foreground/30 mx-auto mb-2" />
            <div className="text-[13px] font-600 text-muted-foreground">No tasks in this category</div>
          </div>
        )}
      </div>
    </div>
  );
}
